// Project list — project CRUD, cards rendering, usage stats
import { t, getDocumentLanguage } from '../shared/i18n.ts';
import { showToast, timeAgo, authenticatedFetch } from '../shared/utils.ts';
import { trackProjectCreated } from '../shared/analytics.ts';
import { isAuthenticated, clearAuthState } from '../shared/auth.ts';
import { showPrompt, showConfirm } from '../shared/custom-dialog.ts';
import { login_path, localized_planner_path } from '../shared/routes';
import { api_v1_projects_path, api_v1_project_path } from '../shared/api-routes';
import { getUnitLabels, getUnits } from '../shared/units.ts';

/**
 * Build a planner URL for a specific project. Resolves the locale from
 * `<html lang>` so the link goes straight to the v2 planner without a
 * 301 hop through `/app` (#1017 phase 2).
 */
function plannerUrlFor(projectId: number | string): string {
  return `${localized_planner_path(getDocumentLanguage())}?project=${projectId}`;
}

/**
 * Render all dashboard sections from the API response.
 */
export function renderDashboard(data: any, { reloadDashboard }: any) {
  // User info
  const nameEl = document.getElementById('userName');
  const tierEl = document.getElementById('userTier');
  if (nameEl) nameEl.textContent = data.profile.name || data.profile.email;
  if (tierEl) {
    tierEl.textContent = data.profile.tier;
    tierEl.className = `tier-badge ${data.profile.tier}`;
  }

  // Usage stats
  renderUsageStats(data.usage);

  // Tier meter (footer card)
  renderTierMeter(data.usage, data.subscription);

  // 14-day activity chart (footer card) — bars + meta caption
  if (data.activity) renderActivity(data.activity);

  // Upgrade banner
  const banner = document.getElementById('upgradeBanner');
  if (banner) {
    banner.hidden = data.profile.tier !== 'free';
  }

  // Projects table
  const grid = document.getElementById('projectsGrid');
  const empty = document.getElementById('projectsEmpty');
  const body = document.getElementById('projectsTableBody');
  if (!grid || !empty) return;

  if (data.projects.length === 0) {
    grid.hidden = true;
    empty.hidden = false;
  } else {
    grid.hidden = false;
    empty.hidden = true;
    if (body) {
      while (body.firstChild) body.removeChild(body.firstChild);
      data.projects.forEach((project: any, index: number) => {
        body.appendChild(renderProjectRow(project, index, reloadDashboard));
      });
    }
  }
}

/**
 * Render the CAD usage stats strip — 4 titleblock cells.
 * Mirrors the design mockup: big serif number + mono marker + sub-caption.
 * Data-wired cells: Projects, Sprinklers placed. Placeholders: Total area, CU avg.
 */
function renderUsageStats(usage: any) {
  const container = document.getElementById('usageStats');
  if (!container) return;
  while (container.firstChild) container.removeChild(container.firstChild);

  const projectsLimitText = usage.projects_limit === -1 ? '∞' : String(usage.projects_limit);
  const cells = [
    {
      key: 'projects',
      marker: t('dashboard.projects') || 'Projects',
      value: `${usage.projects_used}/${projectsLimitText}`,
      caption: t('dashboard.projectsCaption') || 'projects on file'
    },
    {
      key: 'sprinklers',
      marker: t('dashboard.sprinklers') || 'Sprinklers',
      value: String(usage.sprinklers_used),
      caption: t('dashboard.sprinklersCaption') || 'placed'
    },
    {
      key: 'area',
      marker: t('dashboard.totalArea') || 'Total area',
      value: '—',
      caption: getUnitLabels(getUnits()).area
    },
    {
      key: 'cu',
      marker: t('dashboard.cuAvg') || 'CU avg',
      value: '—',
      caption: '%'
    }
  ];

  cells.forEach(cell => {
    container.appendChild(renderStatCell(cell));
  });
}

/**
 * Render a single stat-strip cell — CAD titleblock style.
 * Marker (mono, red) · big serif value · mono caption.
 */
function renderStatCell({ key, marker, value, caption }: { key: string; marker: string; value: string; caption: string }) {
  const cell = document.createElement('div');
  cell.className = `stat-card stat-card-${key}`;

  const markerEl = document.createElement('div');
  markerEl.className = 'stat-card-marker';
  markerEl.textContent = `▸ ${marker.toUpperCase()}`;
  cell.appendChild(markerEl);

  const valueEl = document.createElement('div');
  valueEl.className = 'stat-card-number';
  valueEl.textContent = value;
  cell.appendChild(valueEl);

  const captionEl = document.createElement('div');
  captionEl.className = 'stat-card-caption';
  captionEl.textContent = caption;
  cell.appendChild(captionEl);

  return cell;
}

/**
 * Render the tier limits footer card using data from /api/v1/dashboard.
 */
function renderTierMeter(usage: any, subscription: any) {
  const container = document.getElementById('tierMeterRows');
  if (!container) return;
  while (container.firstChild) container.removeChild(container.firstChild);

  const fmt = (used: number, limit: number) => {
    const limText = limit === -1 ? '∞' : String(limit);
    return `${used} / ${limText}`;
  };

  // Dashboard::Assemble formats the limit in the user's units ("2.47 ac").
  const areaLimit = usage.area_limit_m2 === -1 ? '∞' : (usage.area_limit_display || '—');
  const rows = [
    { label: t('dashboard.projects') || 'Projects', value: fmt(usage.projects_used, usage.projects_limit) },
    { label: t('dashboard.area') || 'Area limit', value: areaLimit },
    { label: t('dashboard.sprinklers') || 'Sprinklers', value: String(usage.sprinklers_used) },
    { label: t('dashboard.zones') || 'Zones', value: String(usage.zones_used) },
    { label: t('dashboard.plan') || 'Plan', value: (subscription && subscription.plan ? String(subscription.plan).toUpperCase() : '—') }
  ];

  rows.forEach(row => {
    const rowEl = document.createElement('div');
    rowEl.className = 'tier-meter-row';

    const label = document.createElement('span');
    label.className = 'tier-meter-label';
    label.textContent = row.label;
    rowEl.appendChild(label);

    const value = document.createElement('b');
    value.className = 'tier-meter-value';
    value.textContent = row.value;
    rowEl.appendChild(value);

    container.appendChild(rowEl);
  });
}

/**
 * Render the 14-day activity bar chart (`#activityChart`) and its
 * caption (`#activityMeta`). Bars are scaled so the tallest matches
 * the chart-area height; an all-zero payload renders no bars and the
 * caption shows the empty-state copy.
 *
 * `activity.buckets`  Array<14> — counts per day, oldest → newest.
 * `activity.summary`  { project_updates, pdf_exports, sketch_runs, total }.
 */
export function renderActivity(activity: any) {
  const svg = document.getElementById('activityChart');
  if (svg) {
    // Strip previously-rendered bars (rect[fill="var(--blue)"]) so re-renders
    // on dashboard reload don't accumulate.
    svg.querySelectorAll('rect.activity-bar').forEach((el) => el.remove());

    const buckets: number[] = activity.buckets || [];
    const max = Math.max(1, ...buckets);
    const chartH = 160;
    const barW = 28;
    const gap = 38;
    const xOffset = 14;

    buckets.forEach((count: number, i: number) => {
      const h = Math.round((count / max) * chartH);
      const x = xOffset + i * gap;
      const y = 160 - h;
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('class', 'activity-bar');
      rect.setAttribute('x', String(x));
      rect.setAttribute('y', String(y));
      rect.setAttribute('width', String(barW));
      rect.setAttribute('height', String(h));
      rect.setAttribute('fill', 'var(--blue)');
      rect.setAttribute('fill-opacity', '0.82');
      svg.appendChild(rect);
    });
  }

  const meta = document.getElementById('activityMeta');
  if (meta) {
    const s = activity.summary || {};
    const parts: string[] = [];
    if (s.project_updates) parts.push(`${s.project_updates} ${t('dashboard.activityProjectUpdates') || 'project updates'}`);
    if (s.pdf_exports)     parts.push(`${s.pdf_exports} ${t('dashboard.activityPdfExports') || 'PDF exports'}`);
    if (s.sketch_runs)     parts.push(`${s.sketch_runs} ${t('dashboard.activitySketchRuns') || 'sketch runs'}`);
    meta.textContent = parts.length > 0
      ? parts.join(' · ')
      : (t('dashboard.activityEmpty') || 'No activity in the last 14 days');
  }
}

/**
 * Render a single project row for the CAD-style table.
 * Columns: # / Project / Location / Area / CU / Status / Updated / → action.
 * Keeps the `.project-card` class + `data-testid="project-row"` so existing
 * E2E selectors continue to work.
 */
function renderProjectRow(project: any, index: number, reloadDashboard: any) {
  const row = document.createElement('div');
  row.className = 'project-card project-row';
  row.setAttribute('role', 'row');
  row.dataset.testid = 'project-row';
  row.dataset.projectId = String(project.id);

  // Derive a DRAFT/LIVE/DONE-ish status from the data we have today.
  // The mockup shows status pills (draft/review/active/archived); until a real
  // status field ships we bucket by content: empty → draft, boundary-only →
  // review, sprinklers+zones → active.
  let status: 'draft' | 'review' | 'active' = 'draft';
  if (project.sprinkler_count > 0 && project.zone_count > 0) status = 'active';
  else if (project.sprinkler_count > 0 || project.zone_count > 0) status = 'review';

  const relTime = timeAgo(project.updated_at, t);
  const seq = String(index + 1).padStart(3, '0');

  // Cells — all built with DOM APIs (no innerHTML) so user-provided project
  // name can never inject markup.
  const numCell = buildCell('cell cell-num', seq);
  const nameCell = buildCell('cell cell-name', project.name || '—');
  const locCell = buildCell('cell cell-location', '—');
  const areaCell = buildCell('cell cell-area', '—');
  const cuCell = buildCell('cell cell-cu', '—');

  const statusCell = document.createElement('div');
  statusCell.className = 'cell cell-status';
  statusCell.setAttribute('role', 'cell');
  const pill = document.createElement('span');
  pill.className = `status-pill status-${status}`;
  pill.textContent = `● ${status.toUpperCase()}`;
  statusCell.appendChild(pill);

  const updatedCell = buildCell('cell cell-updated', relTime);

  const actionCell = document.createElement('div');
  actionCell.className = 'cell cell-action';
  actionCell.setAttribute('role', 'cell');
  const openLink = document.createElement('a');
  openLink.href = plannerUrlFor(project.id);
  openLink.className = 'row-open-link btn-primary btn-sm';
  const openLabel = t('dashboard.open') || 'open';
  openLink.textContent = `${openLabel.toLowerCase()} →`;
  actionCell.appendChild(openLink);

  const deleteBtn = document.createElement('button');
  deleteBtn.className = 'row-delete btn-danger';
  deleteBtn.type = 'button';
  deleteBtn.setAttribute('aria-label', `${t('dashboard.delete') || 'delete'} ${project.name || ''}`.trim());
  deleteBtn.textContent = '×';
  actionCell.appendChild(deleteBtn);

  [numCell, nameCell, locCell, areaCell, cuCell, statusCell, updatedCell, actionCell]
    .forEach(cell => row.appendChild(cell));

  // Open project on row body click (but not on action cell)
  row.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('.cell-action')) return;
    window.location.href = plannerUrlFor(project.id);
  });

  // Delete button
  deleteBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    deleteProject(project.id, project.name, reloadDashboard);
  });

  return row;
}

function buildCell(className: string, text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = className;
  el.setAttribute('role', 'cell');
  el.textContent = text;
  return el;
}

/**
 * Create a new project via the API.
 */
export async function createProject() {
  const name = await showPrompt(t('dashboard.newProjectName'));
  if (!name || !name.trim()) return;

  if (!isAuthenticated()) {
    window.location.href = login_path();
    return;
  }

  let resp;
  try {
    resp = await authenticatedFetch(api_v1_projects_path(), {
      method: 'POST',
      body: { name: name.trim(), data: {} },
    });
  } catch {
    showToast(t('dashboard.networkError'));
    return;
  }

  if (resp.ok) {
    const project = await resp.json();
    trackProjectCreated();
    window.location.href = plannerUrlFor(project.id);
  } else if (resp.status === 401) {
    clearAuthState();
    window.location.href = login_path();
  } else if (resp.status === 403) {
    const err = await resp.json().catch(() => ({}));
    showToast(err.details || t('dashboard.tierLimitReached'), 'warning');
  } else if (resp.status === 400 || resp.status === 422) {
    const err = await resp.json().catch(() => ({}));
    showToast(err.error || t('dashboard.createError'));
  } else {
    showToast(t('dashboard.createError'));
  }
}

/**
 * Delete a project after user confirmation.
 */
async function deleteProject(id: any, name: any, reloadDashboard: any) {
  if (!await showConfirm(t('dashboard.deleteConfirm', { name }))) return;

  let resp;
  try {
    resp = await authenticatedFetch(api_v1_project_path(id), { method: 'DELETE' });
  } catch (err) {
    console.error('Failed to delete project:', err);
    showToast(t('dashboard.networkError'));
    return;
  }

  // The response used to go unchecked, so a 403 (or any other refusal)
  // looked like a successful delete until the row came back on reload.
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    showToast(err.error || err.details || t('dashboard.networkError'));
    return;
  }

  reloadDashboard();
}
